<template>
  <div class="notifications-bell-wrapper" v-if="visible">
    <AppHeaderDropdown right no-caret class="notifications-dropdown">
      <template slot="header">
        <div class="notifications-bell-trigger">
          <AppIcon
            :icon="unreadCount ? 'bell-fill' : 'bell'"
            font-scale="1.25"
            weight="450"
          ></AppIcon>
          <span v-if="unreadCount" class="notifications-bell-badge">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
        </div>
      </template>
      <template slot="dropdown">
        <div class="notifications-panel-header" @click.stop>
          <span class="notifications-panel-title">Notificaciones</span>
          <div class="notifications-panel-header-actions">
            <a href="#"
               class="notifications-panel-mark-all"
               v-if="unreadCount"
               @click.stop.prevent="markAllRead()">
              Marcar todas como leídas
            </a>
            <button type="button"
                    class="notifications-push-toggle"
                    v-if="push.supported"
                    :class="push.subscribed ? 'notifications-push-toggle--on' : 'notifications-push-toggle--off'"
                    v-b-tooltip.hover.left
                    :title="push.subscribed
                      ? 'Notificaciones push activadas en este dispositivo. Click para desactivarlas.'
                      : 'Notificaciones push desactivadas en este dispositivo. Click para activarlas.'"
                    @click.stop.prevent="togglePush">
              <AppIcon :icon="push.subscribed ? 'toggle-on' : 'toggle-off'" font-scale="1.2"></AppIcon>
            </button>
          </div>
        </div>

        <div v-if="push.supported && push.ready && !push.subscribed && push.permission !== 'denied'"
             class="notifications-push-prompt"
             @click.stop>
          <AppIcon icon="bell" class="notifications-push-prompt-icon" font-scale="1.15"></AppIcon>
          <div class="notifications-push-prompt-content">
            <strong>Activá las notificaciones push</strong>
            <span>Recibí avisos importantes en este dispositivo.</span>
          </div>
          <button type="button"
                  class="notifications-push-prompt-action"
                  @click.stop.prevent="togglePush">
            Activar
          </button>
        </div>

        <div class="notifications-panel-list">
          <b-dropdown-item v-for="item in items"
                            :key="item.id"
                            class="notification-item"
                            @click="openItem(item)">
            <div class="notification-item-row">
              <b-avatar size="2.25rem" :src="item.icon" icon="bell-fill" variant="light" class="notification-item-icon"></b-avatar>
              <div class="notification-item-body">
                <div class="notification-item-title">{{ item.title }}</div>
                <div class="notification-item-message">{{ plainNotificationMessage(item.message) }}</div>
                <div class="notification-item-meta">
                  {{ formatDate(item.created_at) }}<template v-if="item.module"> · {{ item.module.title }}</template>
                </div>
              </div>
              <AppIcon icon="check2"
                      class="notification-item-mark-read"
                      v-b-tooltip.hover.left
                      title="Marcar como leída"
                      @click.stop.prevent="markRead(item)"></AppIcon>
            </div>
          </b-dropdown-item>

          <b-dropdown-item-button disabled v-if="!loading && !items.length" class="notifications-panel-empty d-flex flex-column align-items-center">
            <AppIcon icon="check2-circle" class="mb-1" font-scale="1.4"></AppIcon>
            <div>Estás al día</div>
          </b-dropdown-item-button>
        </div>

        <b-dropdown-item :to="{ name: 'NotificationsStaffInbox' }"
                         class="notifications-panel-footer"
                         link-class="notifications-panel-footer-link">
          <span>Ver todas las notificaciones</span>
          <AppIcon icon="chevron-right" font-scale="0.8"></AppIcon>
        </b-dropdown-item>
      </template>
    </AppHeaderDropdown>
    <div v-if="push.supported && push.ready && !push.subscribed && push.permission !== 'denied' && !pushPromptDismissed"
         class="notifications-push-login-prompt"
         @click.stop>
      <button type="button"
              class="notifications-push-login-prompt-close"
              aria-label="Cerrar aviso"
              @click.stop="pushPromptDismissed = true">
        <AppIcon icon="x" font-scale="0.8"></AppIcon>
      </button>
      <AppIcon icon="bell" class="notifications-push-login-prompt-icon" font-scale="1.2"></AppIcon>
      <div class="notifications-push-login-prompt-content">
        <strong>Suscribite a las notificaciones push</strong>
        <span>Recibí avisos importantes aunque no estés mirando la aplicación.</span>
      </div>
      <button type="button"
              class="notifications-push-login-prompt-action"
              @click.stop.prevent="togglePush">
        Activar
      </button>
    </div>
  </div>
</template>

<script>
import serviceAPI from './services'
import Session from '@/handler/session'
import { EventBus } from '@/handler/event-bus'
import Profiles from '@/config/profiles'
import moment from 'moment'
import { plainNotificationMessage } from '@/handler/notificationMessage'
import { HeaderDropdown as AppHeaderDropdown } from '@coreui/vue'
import { getPushPermissionStatus, synchronizePushPermission, isPushSubscribed, isPushSystemActive, autoSubscribeIfNeeded, enablePush, disablePush } from '@/handler/notifications'

const POLL_INTERVAL_MS = 30000

export default {
  name: 'NotificationsBell',
  components: {
    AppHeaderDropdown,
  },
  data: () => {
    return {
      visible: false,
      loading: false,
      unreadCount: 0,
      items: [],
      pollTimer: null,
      push: {
        supported: false,
        subscribed: false,
        permission: 'default',
        ready: false,
      },
      pushPromptDismissed: false,
    }
  },
  created () {
    EventBus.$on('Auth:auth', () => {
      this.setup()
    })
  },
  mounted() {
    window.addEventListener('push-subscription-changed', this.refreshPushStatus)
    this.setup()
  },
  beforeDestroy() {
    this.stopPolling()
    window.removeEventListener('push-subscription-changed', this.refreshPushStatus)
  },
  methods: {
    plainNotificationMessage,
    setup () {
      this.stopPolling()

      var session = Session.getSession()

      if (session.auth && session.auth.user.profile_id == Profiles.PERSONAL && this.hasModuleAccess(session)) {
        this.visible = true
        this.pushPromptDismissed = false
        this.loading = true
        this.fetchUnreadCount()
        this.fetchLast()
        this.startPolling()
        this.setupPushStatus()
      } else {
        this.visible = false
      }
    },
    hasModuleAccess(session) {
      var menu = session.auth.user.permissions && session.auth.user.permissions.menu
      if (!menu) return false

      for (const prop in menu) {
        if (menu[prop].router == 'notifications') return true
      }
      return false
    },
    startPolling() {
      this.pollTimer = setInterval(() => {
        this.fetchUnreadCount()
      }, POLL_INTERVAL_MS)
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    fetchUnreadCount() {
      serviceAPI.getNotificationsUnreadCount()
        .then((response) => {
          var count = response.data.count

          if (count > this.unreadCount) {
            this.playBeep()
            this.fetchLast()
          }

          this.unreadCount = count
        })
        .catch(() => {})
    },
    fetchLast() {
      serviceAPI.getNotificationsLast()
        .then((response) => {
          this.items = response.data
          this.loading = false
        })
        .catch(() => {
          this.loading = false
        })
    },
    openItem(item) {
      // Sin link: abre el detalle de la notificación en la bandeja (el
      // usuario la está viendo, así que se marca como leída).
      if (!item.url) {
        this.markRead(item).finally(() => {
          this.$router.push({ name: 'NotificationsStaffInbox', query: { id: item.id } }).catch(() => {})
        })
        return
      }

      this.markRead(item).finally(() => {
        this.openUrl(item.url)
      })
    },
    // Link interno del sistema: navega en la misma pestaña. Link externo
    // (otro dominio): se abre en una pestaña nueva para no perder el contexto actual.
    openUrl(url) {
      let isExternal = false
      try {
        isExternal = new URL(url, window.location.origin).hostname !== window.location.hostname
      } catch (e) {
        isExternal = false
      }

      if (isExternal) {
        window.open(url, '_blank')
      } else {
        window.location.href = url
      }
    },
    markRead(item) {
      if (item.read) {
        return Promise.resolve()
      }

      return serviceAPI.markNotificationRead(item.id)
        .then(() => {
          this.unreadCount = Math.max(0, this.unreadCount - 1)
          this.items = this.items.filter(i => i.id !== item.id)
        })
    },
    markAllRead() {
      serviceAPI.markAllNotificationsRead()
        .then(() => {
          this.items = []
          this.unreadCount = 0
        })
    },
    playBeep() {
      try {
        var AudioContext = window.AudioContext || window.webkitAudioContext
        var ctx = new AudioContext()
        var oscillator = ctx.createOscillator()
        var gain = ctx.createGain()

        oscillator.connect(gain)
        gain.connect(ctx.destination)

        oscillator.type = 'sine'
        oscillator.frequency.value = 880
        gain.gain.setValueAtTime(0.15, ctx.currentTime)
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.3)

        oscillator.start()
        oscillator.stop(ctx.currentTime + 0.3)
      } catch (e) {
        // El navegador puede bloquear audio hasta la primera interacción del usuario
      }
    },
    formatDate(date) {
      return moment(date).fromNow()
    },

    // NOTIFICACIONES PUSH
    async setupPushStatus() {
      this.push.ready = false
      const status = await synchronizePushPermission(true)
      this.push.permission = status
      if (status === 'unsupported') {
        this.push.supported = false
        this.push.ready = true
        return
      }

      this.push.supported = await isPushSystemActive()
      if (!this.push.supported) {
        this.push.ready = true
        return
      }

      if (status === 'granted') {
        await autoSubscribeIfNeeded()
      }

      this.push.subscribed = status === 'granted' && await isPushSubscribed()
      this.push.ready = true

      if (status === 'denied') {
        this.$awn.warning('El navegador bloqueó las notificaciones para este sitio. Habilitalas desde la configuración del navegador para recibir avisos.')
      }
    },
    refreshPushStatus() {
      if (this.visible) this.setupPushStatus()
    },
    async togglePush() {
      try {
        if (this.push.subscribed) {
          await disablePush()
          this.push.subscribed = false
          this.$awn.success('Notificaciones push desactivadas en este dispositivo')
          return
        }

        if (getPushPermissionStatus() === 'denied') {
          this.$awn.warning('El navegador tiene las notificaciones bloqueadas para este sitio. Para activarlas, entrá a la configuración del sitio (ícono al lado de la URL) y permití las notificaciones.')
          return
        }

        await enablePush()
        this.push.subscribed = true
        this.$awn.success('Notificaciones push activadas')
      } catch (error) {
        if (error && error.name === 'NotAllowedError') {
          this.$awn.warning('El navegador tiene las notificaciones bloqueadas para este sitio. Para activarlas, entrá a la configuración del sitio (ícono al lado de la URL) y permití las notificaciones.')
        } else {
          this.$awn.alert('No se pudo cambiar el estado de las notificaciones push.')
        }
      }
    },
  },
}
</script>

<style scoped>
.notifications-bell-wrapper {
  position: relative;
}
.notifications-push-login-prompt {
  position: absolute;
  top: 43px;
  right: 0;
  z-index: 1050;
  display: flex;
  align-items: center;
  gap: 9px;
  width: 290px;
  padding: 12px 12px 12px 13px;
  border: 1px solid var(--app-color-primary-border);
  border-radius: 9px;
  background-color: var(--app-color-surface);
  box-shadow: var(--app-shadow-md);
}
.notifications-push-login-prompt::before {
  content: '';
  position: absolute;
  top: -6px;
  right: 16px;
  width: 11px;
  height: 11px;
  border-top: 1px solid var(--app-color-primary-border);
  border-left: 1px solid var(--app-color-primary-border);
  background-color: var(--app-color-surface);
  transform: rotate(45deg);
}
.notifications-push-login-prompt-close {
  position: absolute;
  top: 5px;
  right: 5px;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--app-color-text-muted);
  cursor: pointer;
}
.notifications-push-login-prompt-close:hover {
  background-color: var(--app-color-primary-soft);
  color: var(--app-color-text);
}
.notifications-push-login-prompt-icon {
  flex: 0 0 auto;
  color: var(--app-color-primary);
}
.notifications-push-login-prompt-content {
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  flex-direction: column;
  gap: 3px;
  padding-right: 8px;
  color: var(--app-color-text);
}
.notifications-push-login-prompt-content strong {
  font-size: 11px;
  line-height: 1.25;
}
.notifications-push-login-prompt-content span {
  font-size: 10px;
  line-height: 1.3;
  color: var(--app-color-text-muted);
}
.notifications-push-login-prompt-action {
  flex: 0 0 auto;
  padding: 6px 9px;
  border: 0;
  border-radius: 5px;
  background-color: var(--app-color-primary);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  cursor: pointer;
}
.notifications-push-login-prompt-action:hover {
  background-color: var(--app-color-primary-hover);
}
/* En mobile el cartel quedaba chico para tocarlo con comodidad — se agranda
   solo por debajo de md; en desktop se deja como estaba. */
@media (max-width: 767.98px) {
  .notifications-push-login-prompt {
    width: 330px;
    max-width: calc(100vw - 24px);
    gap: 11px;
    padding: 15px 15px 15px 16px;
  }
  .notifications-push-login-prompt-close {
    width: 22px;
    height: 22px;
  }
  .notifications-push-login-prompt-icon {
    font-size: 1.7em !important;
  }
  .notifications-push-login-prompt-content strong {
    font-size: 13px;
  }
  .notifications-push-login-prompt-content span {
    font-size: 12px;
  }
  .notifications-push-login-prompt-action {
    padding: 8px 12px;
    font-size: 12px;
  }
}
.notifications-bell-trigger {
  position: relative;
  width: 34px;
  height: 34px;
  margin: 0 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: var(--app-color-icon);
  transition: background-color 0.15s ease;
}
.notifications-bell-trigger:hover {
  background-color: var(--app-color-primary-soft);
  color: var(--app-color-primary);
}
.notifications-bell-badge {
  position: absolute;
  top: 0px;
  right: 0px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background: var(--app-color-danger);
  color: var(--app-color-on-primary);
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
  box-shadow: 0 0 0 2px var(--app-color-surface);
}
</style>

<style>
.notifications-dropdown .dropdown-menu {
  /* right:auto corta el conflicto con el right:0 de .dropdown-menu-right:
     Popper ya posiciona con left+transform — con los dos activos a la vez
     el ancho queda sobre-restringido y, según el ancho del viewport, el
     panel puede terminar pegado al borde izquierdo de la pantalla en vez
     de junto al ícono (visto en mobileOrderCreate.vue, layout más ancho
     que el de las pantallas CRUD estándar donde no se notaba). */
  right: auto;
  min-width: 360px;
  width: 360px;
  padding: 0;
  overflow: hidden;
}
.notifications-dropdown .dropdown-menu {
  background: var(--app-color-surface);
  box-shadow: var(--app-shadow-md);
}
/* Mismo conector que RemindersQuickAdd.vue/MessagesWidget.vue/
   LocationStatusIndicator.vue: el panel se alinea por su borde derecho
   contra el propio ícono, pero al ser mucho más ancho que el ícono (y con
   más burbujas a la derecha en mobile) queda visualmente desconectado.
   Offsets calculados y verificados en vivo (getBoundingClientRect) contra
   el trigger real: mismo width/margin que las otras burbujas. */
.notifications-dropdown.show::after {
  content: '';
  position: absolute;
  top: 28px;
  right: 21px;
  width: 12px;
  height: 12px;
  background: var(--app-color-surface);
  transform: rotate(45deg);
  box-shadow: -1px -1px 3px rgba(11, 31, 59, 0.08);
  z-index: 1001;
  pointer-events: none;
}
.notifications-dropdown .notifications-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  margin: 0;
  padding: 10px 12px !important;
  border-bottom: 1px solid var(--app-color-border);
}
.notifications-panel-title {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.4;
  color: var(--app-color-text);
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.notifications-panel-header-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex: 0 0 auto;
  margin-left: auto;
  gap: 10px;
}
.notifications-push-prompt {
  display: flex;
  align-items: center;
  gap: 9px;
  margin: 0 10px 8px;
  padding: 10px 11px;
  border: 1px solid var(--app-color-primary-border);
  border-radius: 8px;
  background-color: var(--app-color-primary-soft);
}
.notifications-push-prompt-icon {
  flex: 0 0 auto;
  color: var(--app-color-primary);
}
.notifications-push-prompt-content {
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  flex-direction: column;
  gap: 2px;
  color: var(--app-color-text);
}
.notifications-push-prompt-content strong {
  font-size: 11px;
  line-height: 1.25;
}
.notifications-push-prompt-content span {
  font-size: 10px;
  line-height: 1.25;
  color: var(--app-color-text-muted);
}
.notifications-push-prompt-action {
  flex: 0 0 auto;
  padding: 5px 9px;
  border: 0;
  border-radius: 5px;
  background-color: var(--app-color-primary);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  cursor: pointer;
}
.notifications-push-prompt-action:hover {
  background-color: var(--app-color-primary-hover);
}
.notifications-panel-mark-all {
  font-size: 11px;
  color: var(--app-color-primary);
  white-space: nowrap;
}
.notifications-panel-mark-all:hover {
  color: var(--app-color-primary-hover);
  text-decoration: underline;
}
.notifications-push-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 26px;
  height: 26px;
  padding: 0;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: background-color 0.15s ease, transform 0.1s ease;
}
.notifications-push-toggle:active {
  transform: scale(0.88);
}
.notifications-push-toggle--on {
  background-color: var(--app-color-success-soft);
  color: var(--app-color-success);
}
.notifications-push-toggle--on:hover {
  background-color: var(--app-color-success-border);
}
.notifications-push-toggle--off {
  background-color: var(--app-color-danger-soft);
  color: var(--app-color-danger);
}
.notifications-push-toggle--off:hover {
  background-color: var(--app-color-danger-border);
}
.notifications-panel-list {
  max-height: 380px;
  overflow-y: auto;
}
.notifications-dropdown .notification-item > .dropdown-item {
  padding: 10px 14px;
  border-bottom: 1px solid var(--app-color-border);
  white-space: normal;
}
.notifications-dropdown .notification-item:last-child > .dropdown-item {
  border-bottom: none;
}
.notifications-dropdown .notification-item > .dropdown-item:hover,
.notifications-dropdown .notification-item > .dropdown-item:focus,
.notifications-dropdown .notification-item > .dropdown-item:active,
.notifications-dropdown .notification-item > .dropdown-item.active {
  background-color: var(--app-color-primary-soft) !important;
  color: var(--app-color-primary-active) !important;
}
.notification-item-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}
.notification-item-icon {
  flex: 0 0 auto;
}
.notification-item-body {
  flex: 1;
  min-width: 0;
}
.notification-item-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--app-color-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.notification-item-message {
  font-size: 12px;
  color: var(--app-color-text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  margin-top: 1px;
}
.notification-item-meta {
  font-size: 11px;
  color: var(--app-color-text-subtle);
  margin-top: 4px;
  text-transform: uppercase;
  letter-spacing: 0.2px;
}
.notification-item-mark-read {
  flex: 0 0 auto;
  align-self: center;
  font-size: 14px;
  color: var(--app-color-text-disabled);
  transition: color 0.15s ease;
  cursor: pointer;
}
.notification-item-mark-read:hover {
  color: var(--app-color-primary);
}
.notifications-panel-empty {
  text-align: center;
  padding: 28px 14px;
  color: var(--app-color-text-subtle);
}
.notifications-panel-empty .b-icon {
  color: var(--app-color-text-disabled);
}
.notifications-dropdown .notifications-panel-footer-link,
.notifications-dropdown .notifications-panel-footer .dropdown-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 14px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.2px;
  color: var(--app-color-primary);
  background: var(--app-color-surface-subtle);
  border-top: 1px solid var(--app-color-border);
  text-transform: uppercase;
  transition: background-color 0.15s ease, color 0.15s ease, padding-left 0.15s ease;
}
.notifications-dropdown .notifications-panel-footer-link .b-icon,
.notifications-dropdown .notifications-panel-footer .dropdown-item .b-icon {
  flex: 0 0 auto;
  transition: transform 0.15s ease;
}
.notifications-dropdown .notifications-panel-footer-link:hover,
.notifications-dropdown .notifications-panel-footer-link:focus,
.notifications-dropdown .notifications-panel-footer .dropdown-item:hover,
.notifications-dropdown .notifications-panel-footer .dropdown-item:focus {
  background: var(--app-color-primary-soft);
  color: var(--app-color-primary-hover);
  padding-left: 18px;
  text-decoration: none;
}
.notifications-dropdown .notifications-panel-footer-link:hover .b-icon,
.notifications-dropdown .notifications-panel-footer-link:focus .b-icon,
.notifications-dropdown .notifications-panel-footer .dropdown-item:hover .b-icon,
.notifications-dropdown .notifications-panel-footer .dropdown-item:focus .b-icon {
  transform: translateX(3px);
}

</style>
